/**
 * View state lives in the URL.
 *
 * Filters, sort and the active saved view are all query params, which means a
 * filtered list is shareable, the back button works, and there is no client
 * store to keep in sync with what the server actually ran.
 */

import { z } from 'zod'
import { viewFilter, type ViewFilter } from '@erp/contracts'

const filterArray = z.array(viewFilter)

/**
 * A malformed `filters` param yields an empty list rather than throwing: the URL
 * is user-editable, and a typo should show an unfiltered list, not an error
 * page. The server validates properly when the filters are real.
 */
export function readFilters(params: URLSearchParams): ViewFilter[] {
  const raw = params.get('filters')
  if (raw === null) return []
  try {
    const parsed = filterArray.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : []
  } catch {
    return []
  }
}

/** Returns a new URLSearchParams; never mutates the caller's. */
export function withFilters(params: URLSearchParams, filters: readonly ViewFilter[]): URLSearchParams {
  const next = new URLSearchParams(params)
  if (filters.length === 0) next.delete('filters')
  else next.set('filters', JSON.stringify(filters))
  // Any change to the filter set invalidates the current page cursor.
  next.delete('cursor')
  return next
}

/**
 * The filters to send to the API, or undefined when there are none. Returns the
 * parsed array — turning it into a query param is the API client's job, since
 * that is where every other serialisation decision lives.
 */
export function filtersParam(params: URLSearchParams): ViewFilter[] | undefined {
  const filters = readFilters(params)
  return filters.length > 0 ? filters : undefined
}

export function activeViewId(params: URLSearchParams): string | null {
  return params.get('view')
}
